import React, { Fragment, useState } from 'react'
import { Mutation, ApolloConsumer } from 'react-apollo'
import gql from 'graphql-tag'
import { FormattedMessage, useIntl } from 'react-intl'
import { Role, RoleSwitch } from '../../roles'
import { injector } from '../../../ng-injector'
import { SendToEmailModal } from 'contrastBase/Modals'
import { SendActionMenu } from './send-action-menu'
import { PureQueryOptions } from 'apollo-client'
import { logError, logSuccess } from '../../toaster'
import { EmailDomainsQuery } from '../../security/email-domains-query'
import {
  dateStringToMillis,
  areVulnerabilitiesFromSameApplication
} from '../shared'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'
import { ActionButton } from '@contrast-security-inc/design-system-actions'
import { IconSend } from '@contrast-security-inc/design-system-foundations'
import { ActionProps } from '../../../legacy-design-system/action/action'

const sendVulnerabilitiesToEmailMutation = gql`
  mutation sendVulnerabilitiesToEmail(
    $organizationID: ID!
    $emailAddress: String!
    $vulnerabilityIDs: [ID!]!
  ) {
    sendVulnerabilitiesToEmail(
      input: {
        organizationID: $organizationID
        emailAddress: $emailAddress
        vulnerabilityIDs: $vulnerabilityIDs
      }
    ) {
      _placeholder
    }
  }
`

interface Props extends Pick<ActionProps, 'size'> {
  readonly applicationRole: Role
  readonly showDisabled?: boolean
  readonly vulnerabilities: Array<{
    readonly id: string
    readonly application: {
      id: string
      isParentApp?: boolean
      parentApplicationId?: string
    }
    readonly reportedToBugTrackerTime?: string | null
  }>
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
  setIsRelatedPopoverOpen?: (isOpen: boolean) => void
  /*
   * if validateIfVulnerabilitiesAreFromSameApplication is true, we will validate that all vulnerabilities belong to
   * the same application, otherwise we will assume that all vulnerabilities belong to the same application.
   *
   * e.g.
   * In the organization grid we need to validate all the vulnerabilities.
   * In the vulnerabilities tab within the application grid, is not necessary to validate the vulnerabilities.
   *
   */
  readonly validateIfVulnerabilitiesAreFromSameApplication?: boolean
}

export function SendAction({
  applicationRole,
  showDisabled,
  vulnerabilities,
  organizationID,
  refetchQueries,
  refresh,
  setIsRelatedPopoverOpen,
  validateIfVulnerabilitiesAreFromSameApplication,
  size
}: Props) {
  const { formatMessage } = useIntl()
  const [showEmailDialog, setShowEmailDialog] = useState(false)

  return (
    <ApolloConsumer>
      {(apolloClient) => (
        <ContrastFormattedMessage
          id="detail_vulnerabilities_successfullySentVulnerabilityEmail"
          values={{ count: vulnerabilities.length }}
          children={(successMessage) => (
            <ContrastFormattedMessage
              id="detail_vulnerabilities_errorSendingVulnerabilityEmail"
              values={{ count: vulnerabilities.length }}
              children={(errorMessage) => (
                <Fragment>
                  <RoleSwitch
                    requiredRole="EDIT"
                    role={applicationRole}
                    renderDenied={
                      showDisabled
                        ? () => (
                            <ActionButton
                              isDisabled
                              TooltipProps={{
                                body: (
                                  <FormattedMessage id="title_vulnerabilities_insufficientSendPermissions" />
                                )
                              }}
                              IconButtonProps={{
                                Icon: IconSend,
                                size,
                                'aria-label': 'Send'
                              }}
                            />
                          )
                        : undefined
                    }
                    renderAllowed={() => (
                      <SendActionMenu
                        size={size}
                        setIsRelatedPopoverOpen={setIsRelatedPopoverOpen}
                        onSelectEmailOption={() => setShowEmailDialog(true)}
                        vulnerabilitiesAreFromSameApplication={
                          validateIfVulnerabilitiesAreFromSameApplication
                            ? areVulnerabilitiesFromSameApplication(
                                vulnerabilities
                              )
                            : true
                        }
                        vulnerabilityCount={vulnerabilities.length}
                        onSelectBugtrackerOption={() =>
                          onSelectBugtrackerOption(
                            vulnerabilities,
                            refetchQueries,
                            apolloClient,
                            refresh
                          )
                        }
                      />
                    )}
                  />
                  <Mutation<
                    {
                      readonly _placeholder: boolean
                    },
                    {
                      readonly organizationID: string
                      readonly emailAddress: string
                      readonly vulnerabilityIDs: string[]
                    }
                  >
                    mutation={sendVulnerabilitiesToEmailMutation}
                  >
                    {(sendVulnerabilities) =>
                      !showEmailDialog ? null : (
                        <EmailDomainsQuery organizationID={organizationID}>
                          {({ domains }) => {
                            return (
                              <SendToEmailModal
                                title={formatMessage({
                                  id: 'title_vulnerabilities_sendVulnerability'
                                })}
                                isOpen={showEmailDialog}
                                onClose={() => setShowEmailDialog(false)}
                                sendItems={async (emailAddress) => {
                                  try {
                                    await sendVulnerabilities({
                                      variables: {
                                        organizationID,
                                        emailAddress,
                                        vulnerabilityIDs: vulnerabilities.map(
                                          (vulnerability) => vulnerability.id
                                        )
                                      }
                                    })
                                    logSuccess(successMessage)
                                  } catch (error) {
                                    logError(errorMessage)
                                    return error
                                  }
                                }}
                                permittedDomains={domains}
                              />
                            )
                          }}
                        </EmailDomainsQuery>
                      )
                    }
                  </Mutation>
                </Fragment>
              )}
            />
          )}
        />
      )}
    </ApolloConsumer>
  )
}

export function onSelectBugtrackerOption(
  vulnerabilities,
  refetchQueries,
  apolloClient,
  refresh
) {
  if (!vulnerabilities[0]) {
    return
  }

  const application = vulnerabilities[0].application

  const appID = application.parentApplicationId || application.id

  // when lastSent is undefined, angular will not display the time
  const lastSent = vulnerabilities[0].reportedToBugTrackerTime
    ? dateStringToMillis(vulnerabilities[0].reportedToBugTrackerTime)
    : undefined

  injector.invoke([
    'csSendVulnService',
    'BugtrackerFactory',
    async (csSendVulnService, BugTrackerFactory) => {
      const response = await BugTrackerFactory.getBugtrackersByAppId(appID)

      await csSendVulnService.open({
        resolve: {
          lastSent: () => lastSent,
          traces: () => vulnerabilities.map((v) => v.id),
          bugtrackers: () => response.data.bugtrackers,
          appId: () => appID
        }
      }).result

      await Promise.all(
        refetchQueries.map(({ query, variables }) => {
          apolloClient.query({
            query,
            variables,
            fetchPolicy: 'network-only'
          })
        })
      )

      refresh()
    }
  ])
}
